import React, { useState, useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import Scroll from "react-scroll";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTachometer,
  faPlus,
  faArrowLeft,
  faCheckCircle,
  faTicket,
} from "@fortawesome/free-solid-svg-icons";
import { useRecoilState } from "recoil";

import FormErrorBox from "../Reusable/FormErrorBox";
import FormInputField from "../Reusable/FormInputField";
import FormUnixDateField from "../Reusable/FormUnixDateField";
import PageLoadingContent from "../Reusable/PageLoadingContent";
import { topAlertMessageState, topAlertStatusState } from "../../AppState";
import { DateTime } from "luxon";
import { postPromoCodeCreateAPI } from "../../API/PromoCode";
import { useAuthStore } from "../../stores/auth-store";
import toast from "react-hot-toast";

function AddPromoCode() {
  const [topAlertMessage, setTopAlertMessage] =
    useRecoilState(topAlertMessageState);
  const [topAlertStatus, setTopAlertStatus] =
    useRecoilState(topAlertStatusState);
  const user = useAuthStore((store) => store.user);
  const [errors, setErrors] = useState({});
  const [forceURL, setForceURL] = useState("");
  const [isFetching, setFetching] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    limit: null,
    endDate: null,
    discount: null,
    startDate: DateTime.fromISO(new Date().toISOString()).toFormat(
      "yyyy-MM-dd"
    ),
  });

  function onUserAddSuccess(response) {
    toast.success("Promo Code created");
    setForceURL("/promo-code");
  }

  function onUserAddError(apiErr) {
    toast.error(apiErr.message);

    var scroll = Scroll.animateScroll;
    scroll.scrollToTop();
  }

  function onUserAddDone() {
    setFetching(false);
  }

  const onSubmitClick = (e) => {
    setErrors({});
    setFetching(true);

    const postData = {
      ...formData,
      name: formData.name,
      usageLimit: parseInt(formData.limit),
      discount: parseInt(formData.discount),
      endDate: DateTime.fromSeconds(formData.endDate).toFormat("yyyy-MM-dd"),
      userId: user?.id,
    };

    postPromoCodeCreateAPI(
      postData,
      onUserAddSuccess,
      onUserAddError,
      onUserAddDone
    );
  };

  useEffect(() => {
    let mounted = true;

    if (mounted) {
      window.scrollTo(0, 0);
    }

    return () => {
      mounted = false;
    };
  }, []);

  if (forceURL !== "") {
    return <Navigate to={forceURL} />;
  }

  return (
    <>
      <div class="container">
        <section class="section">
          {/* Desktop Breadcrumbs */}
          <nav class="breadcrumb is-hidden-touch" aria-label="breadcrumbs">
            <ul>
              <li>
                <Link to="/dashboard">
                  <FontAwesomeIcon className="fas" icon={faTachometer} />
                  &nbsp;Dashboard
                </Link>
              </li>
              <li class="">
                <Link to="/promo-code" aria-current="page">
                  <FontAwesomeIcon className="fas" icon={faTicket} />
                  &nbsp;Promo Code
                </Link>
              </li>
              <li class="is-active">
                <Link aria-current="page">
                  <FontAwesomeIcon className="fas" icon={faPlus} />
                  &nbsp;New Promo Code
                </Link>
              </li>
            </ul>
          </nav>

          {/* Mobile Breadcrumbs */}
          <nav class="breadcrumb is-hidden-desktop" aria-label="breadcrumbs">
            <ul>
              <li class="">
                <Link to={`/promo-code`} aria-current="page">
                  <FontAwesomeIcon className="fas" icon={faArrowLeft} />
                  &nbsp;Back to Promo Code
                </Link>
              </li>
            </ul>
          </nav>

          {/* Page */}
          <nav class="box">
            <p class="title is-4">
              <FontAwesomeIcon className="fas" icon={faPlus} />
              &nbsp;New Promo Code
            </p>
            {isFetching ? (
              <PageLoadingContent displayMessage={"Please wait"} />
            ) : (
              <>
                <FormErrorBox errors={errors} />
                <div class="fixed-grid">
                  <div class="grid">
                    <FormInputField
                      name="Name"
                      placeholder="Promo Code"
                      className="cell"
                      errorText={errors && errors.name}
                      type="text"
                      maxLength={5}
                      onChange={(e) =>
                        setFormData((prevData) => ({
                          ...prevData,
                          name: e.target.value,
                        }))
                      }
                      maxWidth="350px"
                    />

                    <FormInputField
                      label="Description"
                      name="Description"
                      placeholder="Description"
                      className="cell"
                      errorText={errors && errors.description}
                      type="text"
                      onChange={(e) =>
                        setFormData((prevData) => ({
                          ...prevData,
                          description: e.target.value,
                        }))
                      }
                      isRequired={true}
                      maxWidth="350px"
                    />
                    <FormInputField
                      label="Limit of Usage"
                      name="Limit"
                      className="cell"
                      placeholder="Limit of Usage"
                      errorText={errors && errors.limit}
                      onChange={(e) =>
                        setFormData((prevData) => ({
                          ...prevData,
                          limit: e.target.value,
                        }))
                      }
                      type="number"
                      isRequired={true}
                      maxWidth="350px"
                    />
                    <FormInputField
                      label="Discount"
                      name="Discount"
                      className="cell"
                      placeholder="Discount Percentage"
                      errorText={errors && errors.discount}
                      onChange={(e) =>
                        setFormData((prevData) => ({
                          ...prevData,
                          discount: e.target.value,
                        }))
                      }
                      type="number"
                      isRequired={true}
                      maxWidth="350px"
                    />
                    <FormUnixDateField
                      label="Expiry Date"
                      name="Expiry Date"
                      className="cell"
                      placeholder="Expiry Date"
                      value={formData.endDate}
                      errorText={errors && errors.endDate}
                      onChange={(d) =>
                        setFormData((prevData) => ({
                          ...prevData,
                          endDate: d,
                        }))
                      }
                      isRequired={true}
                      maxWidth="350px"
                    />
                  </div>
                </div>
                <div class="columns">
                  <div class="column">
                    <div class="columns">
                      <div class="column is-half">
                        <Link to={`/promo-code`} class="button is-hidden-touch">
                          <FontAwesomeIcon className="fas" icon={faArrowLeft} />
                          &nbsp;Back
                        </Link>
                        <Link
                          to={`/promo-code`}
                          class="button is-fullwidth is-hidden-desktop"
                        >
                          <FontAwesomeIcon className="fas" icon={faArrowLeft} />
                          &nbsp;Back
                        </Link>
                      </div>
                      <div class="column is-half has-text-right">
                        <button
                          onClick={onSubmitClick}
                          class="button is-primary is-hidden-touch"
                        >
                          <FontAwesomeIcon
                            className="fas"
                            icon={faCheckCircle}
                          />
                          &nbsp;Save
                        </button>
                        <button
                          onClick={onSubmitClick}
                          class="button is-primary is-fullwidth is-hidden-desktop"
                        >
                          <FontAwesomeIcon
                            className="fas"
                            icon={faCheckCircle}
                          />
                          &nbsp;Save
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </nav>
        </section>
      </div>
    </>
  );
}

export default AddPromoCode;
